import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Camera, User } from 'lucide-react';
import Layout from '../components/Layout';

const ProfilePage = () => {
  const { user, updateProfile, uploadProfilePicture } = useAuth();
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [avatarPreview, setAvatarPreview] = useState(user?.profile_picture || null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    setUploadingAvatar(true);
    try {
      await uploadProfilePicture(file);
      toast.success('Profile picture updated!');
    } catch {
      toast.error('Failed to upload picture');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ display_name: displayName, username });
      toast.success('Profile saved!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const initials = (user?.display_name || user?.email || 'U')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="max-w-lg mx-auto py-10 px-4">
      <h1 className="text-2xl font-heading font-bold mb-2">Profile</h1>
      <p className="text-sm mb-8">
        Your profile syncs with{' '}
        <a
          href="https://mando.kybersuite.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--win-titlebar-start)] underline"
        >
          MandoMaster
        </a>{' '}
        automatically.
      </p>

      {/* Avatar */}
      <div className="flex justify-center mb-8">
        <div className="relative group">
          <div
            className="w-24 h-24 rounded-full bg-[var(--win-window-bg)] border-2 border-[var(--win-shadow)] flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold">{initials}</span>
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full">
              <Camera className="w-6 h-6 text-white" />
            </div>
          </div>
          {uploadingAvatar && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={handleAvatarChange}
        />
      </div>

      {/* Form */}
      <form onSubmit={handleSave} className="win-raised p-6 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="displayName">Display Name</Label>
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
            className="h-11"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="username">Username</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">@</span>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              placeholder="handle"
              className="h-11 pl-7"
            />
          </div>
          <p className="text-xs">Lowercase letters, numbers, and underscores only.</p>
        </div>

        <div className="space-y-1 pt-1">
          <Label className="text-xs">Email</Label>
          <p className="text-sm">{user?.email}</p>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="w-full h-11 font-semibold"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            'Save Profile'
          )}
        </Button>
      </form>
    </div>
  );
};

export default ProfilePage;
